import React, { ReactElement, useEffect, useState } from 'react'
import Radom, { Deposit as IDeposit } from '../../state/Radom'
import TimeText from '../../components/TimeText'
import { Container } from '../../components/Animations'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { getMethod, getMethodLogoByTicker, ManagedPaymentMethod } from '../../util/Managed'
import BreadCrumbs from '../../components/Breadcrumbs'
import { useParams, useSearchParams } from 'react-router-dom'
import { Section, SectionBreaker, SectionContent, SectionItem, SubTitle } from '../../components/PaymentDetails'
import CopyButton from '../../components/CopyButton'
import { RADOM_COLORS } from '../../util/Constants'
import Spinner from '../../components/Spinner'

const Deposit = (): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')

  const [isLoading, setIsLoading] = useState(true)
  const [deposit, setDeposit] = useState<IDeposit>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  useEffect(() => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getDeposit(params.id)
      .then(s => {
        setDeposit(s)
        setPaymentMethod(getMethod(
          s.depositMethod.Crypto?.network || s.depositMethod.Fiat?.onrampNetwork || '',
          s.depositMethod.Crypto?.token || s.depositMethod.Fiat?.onrampToken
        ))
      })
      .catch(e => console.error('Failed to look up deposit', e))
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!deposit || !paymentMethod) {
    return <></>
  }

  return <Container>
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', rowGap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: deposit.id, to: `/deposit/${deposit.id}?source=transactions` }
            ]
            : [
              { item: 'Deposits', to: '/deposits' },
              { item: deposit.id, to: '.' }
            ]} />
      </div>

      <div style={{
        padding: 30,
        borderRadius: 10,
        border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
        <div style={{
          fontSize: '18px',
          paddingBottom: 4
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: '32px'
          }}>

            <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
            <span>{deposit.netAmount}</span>
            <span style={{
              background: RADOM_COLORS.GRAY_MED,
              color: RADOM_COLORS.BLACK,
              borderRadius: 10,
              fontSize: 14,
              padding: '4px 10px',
              fontWeight: 400

            }}>{paymentMethod.ticker}</span>

          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          flexWrap: 'wrap',
          gap: 30
        }}>
          <div>
            <SubTitle>ID</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
              <span>{deposit.id}</span>
              <CopyButton text={deposit.id} />
            </div>
          </div>
          <div>
            <SubTitle>Type</SubTitle>
            <span style={{
              fontSize: 14,
              color: RADOM_COLORS.BLACK
            }}>{deposit.depositMethod.Crypto ? 'Cypto deposit' : 'Fiat deposit'}</span>
          </div>
          <div>
            <SubTitle>Deposit date</SubTitle>
            <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
          </div>
        </div>
      </div>

      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Deposit details
        </SubTitle>

        <SectionContent>
          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <PaymentMethodDisplay
                style={{ marginRight: 4 }}
                paymentMethod={paymentMethod}
                method={{ network: deposit.depositMethod.Crypto?.network || 'Fiat' }}
                includeNetworkName
                disableTooltip={false} />
            </div>
          </SectionItem>
          {
            deposit.depositMethod.Fiat &&
            <SectionItem>
              <SubTitle>Fiat gross deposit</SubTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>

                <PaymentMethodDisplay
                  method={{ network: 'Fiat', token: deposit.depositMethod.Fiat.fiatCurrency }}
                  excludeName />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                  <span>{deposit.fiatGrossAmount}</span>
                  <span>{getMethod('Fiat', deposit.depositMethod.Fiat.fiatCurrency).ticker}</span>
                </div>
              </div>
            </SectionItem>
          }

          <SectionItem>
            <SubTitle>Gross deposit</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.grossAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.grossAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Net deposit</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.netAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.netAmount}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
              {
                deposit.depositMethod.Crypto && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network: deposit.depositMethod.Crypto.network,
                      token: deposit.depositMethod.Crypto.token
                    }}
                    excludeName
                    disableTooltip={false} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.fee}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Crypto.network,
                      deposit.depositMethod.Crypto.token).ticker}</span>
                  </div>
                </>
              }

              {
                deposit.depositMethod.Fiat && <>
                  <PaymentMethodDisplay
                    style={{ marginRight: 4 }}
                    method={{
                      network:
                      deposit.depositMethod.Fiat.onrampNetwork,
                      token: deposit.depositMethod.Fiat.onrampToken
                    }}
                    excludeName />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <span>{deposit.fee}</span>
                    <span>{getMethod(
                      deposit.depositMethod.Fiat.onrampNetwork,
                      deposit.depositMethod.Fiat.onrampToken).ticker}</span>
                  </div>
                </>
              }

            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Deposited at</SubTitle>
            <div>
              <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(deposit.depositedAt)} />
            </div>
          </SectionItem>

        </SectionContent>
        <SectionBreaker />

        {
          deposit.conversionRates.length > 0 &&
                    <div>
                      <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Conversion rates</SubTitle>
                      {
                        deposit.conversionRates.map((d, i) =>
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span>1</span>
                              {
                                getMethodLogoByTicker(d.from) &&
                                <img src={getMethodLogoByTicker(d.from)} style={{ width: 15, height: 'auto' }} />
                              }
                              {d.from}
                            </div>
                            <div>=</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span>{Number(d.rate)}</span>
                              {
                                getMethodLogoByTicker(d.to) &&
                                <img src={getMethodLogoByTicker(d.to)} style={{ width: 15, height: 'auto' }} />
                              }
                              {d.to}
                            </div>
                          </div>
                        )}
                    </div>
        }
      </Section>

    </div >
  </Container >
}

export default Deposit
